import { createContext, FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import {
    AccountInfo,
    EventType,
    InteractionRequiredAuthError,
    IPublicClientApplication,
    SilentRequest
} from '@azure/msal-browser';
import { EventMessage } from '@azure/msal-browser/dist/event/EventMessage';
import { useMsal } from '@azure/msal-react';
import { loginRequest, rolesPath } from 'app/config';
import { Roles } from 'common/constants/roles';
import { _request } from 'common/services/api';

type IState = Roles[];
export type ProfileInfo = {
    name: string | null
}

type DataRolesType = {
    id: string;
    name: string;
}

type RolesType = {
    success: boolean;
    error: string;
    data: DataRolesType[];
}

export type AuthContextType = {
    isInAnyRole: boolean;
    isAuthenticated: boolean;
    currentUserRoles: IState;
    rolesChecked: boolean;
    userProfileInfo: ProfileInfo;
}

export const AuthContext = createContext({} as AuthContextType);
export const useAuthContext = () => useContext(AuthContext);

export const getToken = async (instance: IPublicClientApplication, accounts: AccountInfo[]) => {
    const APIaccessTokenRequest = {
        ...loginRequest,
        account: accounts[0]
    };

    await instance.handleRedirectPromise();
    const account = instance.getActiveAccount();

    if (account) {
        return instance.acquireTokenSilent(APIaccessTokenRequest)
            .catch((error: Error) => {
                if (error instanceof InteractionRequiredAuthError) {
                    return instance.acquireTokenRedirect(APIaccessTokenRequest);
                }
                throw error;
            });
    }
    else {
        return instance.loginRedirect(loginRequest);
    }
};

interface Props {
  children: React.ReactNode;
}

export const AuthProvider: FC<Props> = ({ children }) => {

    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [rolesChecked, setRolesChecked] = useState(false);
    const [userProfileInfo, setUserProfileInfo] = useState<ProfileInfo>({
        name: null
    });
    const [currentUserRoles, setCurrentUserRoles] = useState<IState>([Roles.Admin]);
    const { instance, accounts } = useMsal();

    useEffect(() => {
        const callbackId = instance.addEventCallback((event: EventMessage) => {
            if (event.eventType === EventType.LOGIN_SUCCESS && event?.payload) {
                const payload = event.payload as SilentRequest;
                if (payload.account) {
                    instance.setActiveAccount(payload.account);
                }
            }
        });

        return () => {
            if (callbackId) {
                instance.removeEventCallback(callbackId);
            }
        };
    }, [instance]);

    useEffect(() => {
        const requestToken = async () => {
            const response = await getToken(instance, accounts);
            if (response?.accessToken) {
                const account = instance.getActiveAccount();
                setUserProfileInfo(prevProfileInfo => ({
                    ...prevProfileInfo,
                    name: account?.name ?? null,
                }));
                setIsAuthenticated(true);
            } else {
                throw new Error('Could not get access token');
            }
        };

        requestToken().catch(console.error);
    }, [instance, accounts]);

    const getRoles = useCallback(() => (
        _request<RolesType>('GET', rolesPath)
            .then((result) => {
                const roles = result?.data?.map(function (e: DataRolesType) {
                    return e?.name;
                });
                setCurrentUserRoles(roles as Roles[]);
            }).catch(() => {
                // Role fetch failed - user will have default roles
            })
    ), []);

    const isInAnyRole = currentUserRoles?.some(el => Object.values<string>(Roles).includes(el));

    useEffect(() => {
        async function checkRoles() {
            if (isAuthenticated) {
                await getRoles();
                setRolesChecked(true);
            }
        }
        async function obtainUserInfo() {
            if (isAuthenticated) {
                const account = instance.getActiveAccount();
                setUserProfileInfo({
                    name: account?.name ?? null
                });
            }
        }
        checkRoles().catch(console.error);
        obtainUserInfo().catch(console.error);

    }, [isAuthenticated, instance, getRoles]);

    const authContextValue: AuthContextType = useMemo(() => ({
        isInAnyRole,
        isAuthenticated,
        currentUserRoles,
        rolesChecked,
        userProfileInfo
    }), [isInAnyRole, isAuthenticated, currentUserRoles, rolesChecked, userProfileInfo]);

    return (
        <AuthContext.Provider value={authContextValue}>
            {children}
        </AuthContext.Provider>
    );
};
